import { RangeSetBuilder, type Extension } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
} from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";

// BBEL highlighting (#5683). A field is literal text interleaved with `{{ … }}` expression slots.
// Slot colouring is a decoration overlay, not a language: the editor's base language is the field's
// content lang (plain / JSON / XML via BbelField's `lang` prop), and these mark decorations sit above
// its syntax highlighting — so a slot keeps its colour mid-JSON-string, where the JSON parser sees
// only string content. A token-level scan (not a full Lezer grammar, per ADR-017 / D3) is enough:
// literal text stays the base language's while slots get token classes — delimiters, data paths,
// function calls, strings, numbers, keywords — so an expression reads at a glance. Full-fidelity JS
// highlighting inside the slots (parseMixed with @codemirror/lang-javascript, n8n-style) is the
// deferred upgrade; it needs a Lezer host grammar and buys little over this once paths and calls
// are coloured.

/** One coloured span inside a `{{ … }}` slot; `cls` is a .bbel-tok-* class styled in styles.scss. */
export interface SlotToken {
  from: number;
  to: number;
  cls: string;
}

const KEYWORDS_AND_ATOMS = new Set(["const", "typeof", "in", "true", "false", "null", "undefined"]);

// Sticky regexes matched at an explicit index — no per-token slicing.
const NUMBER = /[0-9]+(?:\.[0-9]+)?/y;
const WORD = /[A-Za-z_$][\w$]*/y;
const OPERATOR = /[+\-*/%!<>=?:&|]+/y;

function matchAt(re: RegExp, text: string, at: number): string | null {
  re.lastIndex = at;
  return re.exec(text)?.[0] ?? null;
}

/** End index of a quoted string starting at `at` (whose opening quote is `quote`). */
function scanString(text: string, at: number, quote: string): number {
  let i = at + 1;
  let escaped = false;
  while (i < text.length) {
    const ch = text[i++];
    if (ch === quote && !escaped) break;
    escaped = ch === "\\" && !escaped;
  }
  return i;
}

/**
 * Tokenize every `{{ … }}` slot in `text`. Literal text between slots yields no tokens; an
 * unterminated slot colours to the end of the text (the mid-typing state).
 */
export function slotTokens(text: string): SlotToken[] {
  const tokens: SlotToken[] = [];
  let i = 0;
  while (i < text.length) {
    const open = text.indexOf("{{", i);
    if (open < 0) break;
    tokens.push({ from: open, to: open + 2, cls: "bbel-tok-brace" });
    i = open + 2;

    while (i < text.length) {
      if (text.startsWith("}}", i)) {
        tokens.push({ from: i, to: i + 2, cls: "bbel-tok-brace" });
        i += 2;
        break;
      }
      const ch = text[i];
      if (/\s/.test(ch)) {
        i++;
        continue;
      }
      if (ch === '"' || ch === "'" || ch === "`") {
        const to = scanString(text, i, ch);
        tokens.push({ from: i, to, cls: "bbel-tok-string" });
        i = to;
        continue;
      }
      const number = matchAt(NUMBER, text, i);
      if (number) {
        tokens.push({ from: i, to: i + number.length, cls: "bbel-tok-number" });
        i += number.length;
        continue;
      }
      const word = matchAt(WORD, text, i);
      if (word) {
        const to = i + word.length;
        // The whole data path (root + members) shares one class — it reads as a single reference;
        // an identifier directly before `(` is a call.
        const cls = KEYWORDS_AND_ATOMS.has(word)
          ? "bbel-tok-atom"
          : text[to] === "("
            ? "bbel-tok-fn"
            : "bbel-tok-path";
        tokens.push({ from: i, to, cls });
        i = to;
        continue;
      }
      const operator = matchAt(OPERATOR, text, i);
      if (operator) {
        tokens.push({ from: i, to: i + operator.length, cls: "bbel-tok-op" });
        i += operator.length;
        continue;
      }
      // Dots, commas, brackets, parens — structural punctuation.
      tokens.push({ from: i, to: i + 1, cls: "bbel-tok-op" });
      i++;
    }
  }
  return tokens;
}

function decorate(text: string): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  for (const token of slotTokens(text)) builder.add(token.from, token.to, Decoration.mark({ class: token.cls }));
  return builder.finish();
}

/** The BBEL slot-highlighting overlay — mark decorations over whatever base language is active. */
export function bbelSlotHighlight(): Extension {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = decorate(view.state.doc.toString());
      }
      update(update: ViewUpdate) {
        if (update.docChanged) this.decorations = decorate(update.state.doc.toString());
      }
    },
    { decorations: (v) => v.decorations },
  );
}

// The base language (json/xml) tokens get their own bbel-base-* classes — same Bootstrap-variable
// palette as the slot tokens (so JSON/XML colours follow data-bs-theme), but styled at LOWER CSS
// specificity. A span inside a slot inside a JSON string carries both a base and a slot class; the
// slot class must always win or the expression disappears into the string colour. Registering this
// also supersedes minimalSetup's defaultHighlightStyle (a fallback with fixed light-mode colours).
const baseLangHighlight = HighlightStyle.define([
  { tag: [t.propertyName, t.tagName], class: "bbel-base-key" },
  { tag: [t.string, t.attributeValue], class: "bbel-base-string" },
  { tag: t.number, class: "bbel-base-number" },
  { tag: [t.bool, t.null, t.keyword, t.attributeName], class: "bbel-base-atom" },
  { tag: t.comment, class: "bbel-base-comment" },
]);

/** Theme-aware syntax colours for the base content language (JSON/XML). */
export function bbelBaseLangHighlight(): Extension {
  return syntaxHighlighting(baseLangHighlight);
}
